/**
 * feAI – Frontend
 * src/components/layout/FirstAccessibleRedirect.tsx
 *
 * Reindirizza l'utente alla prima pagina del menu a cui ha accesso.
 * Usato dopo il login e come fallback quando l'utente non può accedere
 * alla pagina richiesta.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMyPermissions } from '../../lib/permissionsApi';

/**
 * Ordine di default delle pagine nel menu di sinistra.
 * Le pagine non admin vengono per prime, poi quelle admin.
 */
const DEFAULT_PAGE_ORDER = [
  'dashboard',
  'agenti-dashboard',
  'users',
  'report-accessi',
  'page-access-report',
  'mail-report',
  'report-apparound',
  'archiviati',
  'partite-aperte',
  'gerarchia-agenti',
  'retention',
  'admin/dettaglio-tabs',
  'admin/permissions',
] as const;

export const FirstAccessibleRedirect: React.FC = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function findFirstPage() {
      try {
        const perms = await fetchMyPermissions();

        if (cancelled) return;

        // Trova la prima pagina abilitata in ordine di menu
        for (const pageId of DEFAULT_PAGE_ORDER) {
          const perm = perms.find(p => p.page_id === pageId);
          if (perm?.can_view && perm?.can_use) {
            navigate(`/${pageId}`, { replace: true });
            return;
          }
        }

        // Se nessuna pagina è accessibile, mostra un messaggio
        if (!cancelled) setChecked(true);
      } catch {
        // Fallback: se l'API permessi non è disponibile, vai su dashboard
        if (!cancelled) {
          navigate('/dashboard', { replace: true });
        }
      }
    }

    findFirstPage();
    return () => { cancelled = true; };
  }, [navigate]);

  if (checked) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 40,
        textAlign: 'center',
        fontFamily: "'DM Sans', sans-serif",
        background: '#F5F7F5',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0F1F18', marginBottom: 8 }}>
          Nessun accesso disponibile
        </div>
        <div style={{ fontSize: '0.88rem', color: '#6b7280', maxWidth: 400, lineHeight: 1.6 }}>
          Al momento non hai i permessi per accedere a nessuna sezione del portale.
          Contatta un amministratore per maggiori informazioni.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: "'DM Sans', sans-serif",
      color: '#9ca3af',
      fontSize: '0.9rem',
      background: '#F5F7F5',
    }}>
      Reindirizzamento in corso...
    </div>
  );
};

export default FirstAccessibleRedirect;